import React, { useState, useEffect, useRef, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { getAttendance, getUsers, Attendance, User, checkOut, getTenant } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getMaxDatetimeLocal, getMaxDateToday } from '../utils/dateUtils';

const MARKER_ICONS = {
  green: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
  red: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
};

/** Custom overlay: compact tooltip div above marker, no Google InfoWindow UI */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- OverlayView types vary by @types version
const OverlayViewBase =
  // Use window.google if available, otherwise fall back to a no-op base class
  ((typeof window !== 'undefined' && (window as any).google?.maps?.OverlayView) as
    | (new () => any)
    | undefined) || (class {} as new () => any);
class CustomInfoOverlay extends OverlayViewBase {
  private div: HTMLDivElement | null = null;
  private position: google.maps.LatLng | null = null;

  onAdd() {
    this.div = document.createElement('div');
    this.div.style.cssText = [
      'position: absolute; left: 0; top: 0;',
      'transform: translate(-50%, -100%) translateY(-12px);',
      'pointer-events: none; white-space: nowrap;',
      'box-shadow: 0 2px 8px rgba(0,0,0,0.2); border-radius: 6px;',
      'padding: 6px 8px; font-family: inherit; font-size: 12px;',
      'max-width: 240px; white-space: normal;',
    ].join(' ');
    this.div.style.display = 'none';
    const panes = this.getPanes();
    if (panes) panes.floatPane.appendChild(this.div);
  }

  draw() {
    if (!this.div || !this.position) return;
    const projection = this.getProjection();
    if (!projection) return;
    const point = projection.fromLatLngToDivPixel(this.position);
    if (!point) return;
    this.div.style.left = `${point.x}px`;
    this.div.style.top = `${point.y}px`;
  }

  onRemove() {
    if (this.div?.parentNode) this.div.parentNode.removeChild(this.div);
    this.div = null;
  }

  setContent(html: string) {
    if (this.div) this.div.innerHTML = html;
  }

  setPosition(latLng: google.maps.LatLng) {
    this.position = latLng;
    this.draw();
  }

  show() {
    if (this.div) this.div.style.display = '';
    this.draw();
  }

  hide() {
    if (this.div) this.div.style.display = 'none';
  }
}

/** Map showing check-in (green) and check-out (red) with custom hover tooltip */
const AttendanceLocationsMap: React.FC<{
  locations: Array<{
    lat: number;
    lng: number;
    label: string;
    color: 'green' | 'red';
    time: string;
    address: string;
    imageUrl?: string;
  }>;
  height?: string;
  isDark?: boolean;
}> = ({ locations, height = '220px', isDark = false }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const customInfoRef = useRef<CustomInfoOverlay | null>(null);

  useEffect(() => {
    if (!locations.length || !mapRef.current) return;

    const initMap = () => {
      if (!window.google?.maps || !mapRef.current) return;
      const first = locations[0];
      const map = new google.maps.Map(mapRef.current, {
        center: new google.maps.LatLng(first.lat, first.lng),
        zoom: 14,
        fullscreenControl: true,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: true,
      });
      mapInstanceRef.current = map;
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
      if (customInfoRef.current) {
        customInfoRef.current.hide();
        const prev = customInfoRef.current as { setMap?: (map: google.maps.Map | null) => void };
        if (typeof prev.setMap === 'function') prev.setMap(null);
      }
      const customInfo = new CustomInfoOverlay();
      const overlayWithSetMap = customInfo as { setMap?: (map: google.maps.Map | null) => void };
      if (typeof overlayWithSetMap.setMap === 'function') {
        overlayWithSetMap.setMap(map);
        customInfoRef.current = customInfo;
      } else {
        customInfoRef.current = null;
      }
      let hideTimeoutId: number | null = null;
      const bounds = new google.maps.LatLngBounds();
      locations.forEach((loc) => {
        const position = new google.maps.LatLng(loc.lat, loc.lng);

        // Resolved marker image: record image, or default start/end marker when not available
        const markerImageUrl = loc.imageUrl
          ? loc.imageUrl
          : loc.color === 'green'
            ? '/images/start_marker.png'
            : '/images/end_marker.png';

        // If we have a custom record image, draw a colored square border behind it
        if (loc.imageUrl) {
          const bgMarker = new google.maps.Marker({
            position,
            map,
            icon: {
              path: 'M -28 -28 L 28 -28 L 28 28 L -28 28 Z',
              fillColor: loc.color === 'green' ? '#dcfce7' : '#fee2e2',
              fillOpacity: 1,
              strokeColor: loc.color === 'green' ? '#22c55e' : '#ef4444',
              strokeWeight: 3,
              scale: 1,
              anchor: new google.maps.Point(0, 0),
            },
          });
          markersRef.current.push(bgMarker);
        }

        // Foreground marker: image (custom or default start_marker / end_marker)
        // Custom images are centered; default pin markers use bottom-center anchor
        const icon = {
          url: markerImageUrl,
          scaledSize: new google.maps.Size(48, 48),
          anchor: loc.imageUrl
            ? new google.maps.Point(24, 24)
            : new google.maps.Point(27, 45),
        };

        const marker = new google.maps.Marker({
          position,
          map,
          title: loc.label,
          icon,
        });
        const bg = isDark ? '#1e293b' : '#ffffff';
        const text = isDark ? '#f1f5f9' : '#1e293b';
        const labelColor = loc.color === 'green' ? '#22c55e' : '#ef4444';
        const contentHtml = [
          `<div style="font-weight:600;margin-bottom:4px;font-size:13px;color:${labelColor}">${loc.label}</div>`,
          `<div style="color:${text}"><strong>Time:</strong> ${loc.time}</div>`,
          `<div style="margin-top:4px;color:${text}"><strong>Location:</strong> ${loc.address || '—'}</div>`,
        ].join('');
        const fullContent = `<div style="background:${bg};color:${text};padding:6px 8px;border-radius:6px">${contentHtml}</div>`;

        marker.addListener('mouseover', () => {
          if (!customInfoRef.current) return;
          if (hideTimeoutId !== null) {
            window.clearTimeout(hideTimeoutId);
            hideTimeoutId = null;
          }
          customInfoRef.current.setContent(fullContent);
          customInfoRef.current.setPosition(position);
          customInfoRef.current.show();
        });

        marker.addListener('mouseout', () => {
          if (!customInfoRef.current) return;
          if (hideTimeoutId !== null) window.clearTimeout(hideTimeoutId);
          hideTimeoutId = window.setTimeout(() => {
            customInfoRef.current?.hide();
          }, 500);
        });
        markersRef.current.push(marker);
        bounds.extend(marker.getPosition()!);
      });
      if (locations.length > 1) {
        map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
      } else {
        map.setCenter(new google.maps.LatLng(first.lat, first.lng));
        map.setZoom(15);
      }
    };

    if (window.google?.maps) {
      initMap();
    } else {
      const t = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(t);
          initMap();
        }
      }, 100);
      return () => clearInterval(t);
    }
    return () => {
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
      if (customInfoRef.current) {
        const prev = customInfoRef.current as { setMap?: (map: google.maps.Map | null) => void };
        if (typeof prev.setMap === 'function') prev.setMap(null);
        customInfoRef.current = null;
      }
      mapInstanceRef.current = null;
    };
  }, [locations, isDark]);

  if (!locations.length) return null;
  return (
    <div
      ref={mapRef}
      style={{
        width: '100%',
        height,
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #ddd',
      }}
    />
  );
};

const AttendanceManagement: React.FC = () => {
  const { theme } = useTheme();
  const { user: currentUser } = useAuth();
  const isDark = theme === 'dark';
  const [attendanceRecords, setAttendanceRecords] = useState<Attendance[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [adminCheckoutRecord, setAdminCheckoutRecord] = useState<Attendance | null>(null);
  const [adminCheckoutLocation, setAdminCheckoutLocation] = useState<{
    latitude: number;
    longitude: number;
    address?: string;
  } | null>(null);
  const [adminCheckoutTime, setAdminCheckoutTime] = useState<string>('');
  const [adminCheckoutLoading, setAdminCheckoutLoading] = useState(false);
  const [adminCheckoutError, setAdminCheckoutError] = useState('');

  const getNowDatetimeLocal = () =>
    new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

  const fetchHospitalDefaultLocation = useCallback(async (): Promise<{
    latitude: number;
    longitude: number;
    address?: string;
  } | null> => {
    if (!currentUser?.tenantId) return null;
    try {
      const response = await getTenant(currentUser.tenantId);
      const tenant = response.data?.data;
      const hospital = tenant?.spec?.hospital;
      if (!hospital) return null;
      const lat = hospital.hospitalLocation?.latitude ?? hospital.latitude;
      const lng = hospital.hospitalLocation?.longitude ?? hospital.longitude;
      if (lat == null || lng == null) return null;
      const addressParts = [
        hospital.name,
        hospital.address,
        hospital.city,
        hospital.state,
        hospital.zipCode,
        hospital.country,
      ].filter(Boolean);
      return {
        latitude: Number(lat),
        longitude: Number(lng),
        address: addressParts.length ? addressParts.join(', ') : undefined,
      };
    } catch (err) {
      console.error('Failed to fetch hospital location for admin checkout', err);
      return null;
    }
  }, [currentUser?.tenantId]);
  // Helper function to get today's date string in YYYY-MM-DD format
  const getTodayString = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  const [selectedDate, setSelectedDate] = useState<string>(() => getTodayString());
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<'checkedIn' | 'checkedOut' | ''>('');
  const [isUserSelectedDate, setIsUserSelectedDate] = useState<boolean>(false);
  const [viewRecord, setViewRecord] = useState<Attendance | null>(null);

  // On mount/refresh, always reset to today's date and show today's data
  useEffect(() => {
    const todayString = getTodayString();
    setSelectedDate(todayString);
    setIsUserSelectedDate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if date has changed (midnight) and update selectedDate if user hasn't manually selected a date
  useEffect(() => {
    const checkDateChange = () => {
      // If user manually selected a date, don't auto-update
      if (isUserSelectedDate) {
        return;
      }

      const todayString = getTodayString();

      // Check if selectedDate is from a previous day and update it to today
      const selectedDateParts = selectedDate.split('-');
      const todayParts = todayString.split('-');

      const selectedDateOnly = new Date(
        parseInt(selectedDateParts[0]),
        parseInt(selectedDateParts[1]) - 1,
        parseInt(selectedDateParts[2])
      );
      const todayOnly = new Date(
        parseInt(todayParts[0]),
        parseInt(todayParts[1]) - 1,
        parseInt(todayParts[2])
      );

      // If selectedDate is from a previous day, update it to today
      if (selectedDateOnly < todayOnly) {
        setSelectedDate(todayString);
        setIsUserSelectedDate(false);
      }
    };

    // Check immediately
    checkDateChange();

    // Set up interval to check every minute for date changes
    const interval = setInterval(checkDateChange, 60000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, isUserSelectedDate]);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedUserId, selectedStatus]);

  const fetchUsers = async () => {
    try {
      const response = await getUsers(1, 1000);
      setUsers(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load users:', err);
    }
  };

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      setError('');

      // Calculate start and end of selected date
      const date = new Date(selectedDate);
      date.setHours(0, 0, 0, 0);
      const startOfDay = date.getTime();
      date.setHours(23, 59, 59, 999);
      const endOfDay = date.getTime();

      const response = await getAttendance(
        1,
        1000,
        selectedUserId || undefined,
        startOfDay,
        endOfDay,
        selectedStatus || undefined
      );

      setAttendanceRecords(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load attendance:', err);
      setError(err.response?.data?.message || 'Failed to load attendance records');
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId: string): string => {
    const user = users.find((u) => (u._id || u.id) === userId);
    return user?.metadata?.name || user?.name || userId;
  };

  const formatTime = (timestamp: number): string => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const formatDateTime = (timestamp: number): string => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDuration = (checkInTime: number, checkOutTime?: number): string => {
    if (!checkOutTime) return 'N/A';
    const duration = checkOutTime - checkInTime;
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  return (
    <DashboardLayout>
      {/* <div className="page-header">
        <h2>Attendance Management</h2>
        <p>View and manage staff attendance records</p>
      </div> */}

      <div className="card" style={{ marginTop: '24px', height: "calc(100vh - 185px)" }}>
        <div style={{ marginBottom: '20px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label>Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setIsUserSelectedDate(true);
              }}
              max={getMaxDateToday()}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid',
                borderColor: isDark ? '#334155' : '#ddd',
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                color: isDark ? '#f1f5f9' : '#1e293b'
              }}
            />
          </div>

          <div className="form-group" style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label>User</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid',
                borderColor: isDark ? '#334155' : '#ddd',
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                color: isDark ? '#f1f5f9' : '#1e293b'
              }}
            >
              <option value="">All Users</option>
              {users.map((user) => (
                <option key={user._id || user.id} value={user._id || user.id}>
                  {user.metadata?.name || user.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ flex: '1 1 150px', minWidth: '150px' }}>
            <label>Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as 'checkedIn' | 'checkedOut' | '')}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid',
                borderColor: isDark ? '#334155' : '#ddd',
                borderRadius: '6px',
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                color: isDark ? '#f1f5f9' : '#1e293b'
              }}
            >
              <option value="">All Status</option>
              <option value="checkedIn">Checked In</option>
              <option value="checkedOut">Checked Out</option>
            </select>
          </div>

          <button
            onClick={fetchAttendance}
            disabled={loading}
            className="refresh-button"
            title="Refresh attendance records"
            style={{ marginBottom: '23px' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

        {loading ? (
          <div className="loading">Loading attendance records...</div>
        ) : attendanceRecords.length === 0 ? (
          <div className="empty-state">
            <h3>No attendance records found</h3>
            <p>No attendance records for the selected date and filters</p>
          </div>
        ) : (
          <div className="table-container" style={{ height: 'calc(100vh - 320px)', overflowY: 'auto' }}>
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>User</th>
                  {/* <th>Date</th> */}
                  <th>Check In Time</th>
                  <th>Check In Image</th>
                  <th>Check In Location</th>
                  <th>Check Out Time</th>
                  <th>Check Out Image</th>
                  <th>Check Out Location</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {attendanceRecords.map((record) => (
                  <tr
                    key={record._id}
                    onClick={() => setViewRecord(record)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{getUserName(record.spec.userId)}</td>
                    {/* <td>{formatDate(record.spec.checkIn.time)}</td> */}
                    <td>{formatDateTime(record.spec.checkIn.time)}</td>
                    <td>
                      {record.spec.checkIn.image ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <img
                            src={record.spec.checkIn.image}
                            alt="Check-in"
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border: '1px solid #ddd',
                              cursor: 'pointer',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(record.spec.checkIn.image, '_blank');
                            }}
                            title="Click to view full image"
                          />
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                      )}
                    </td>
                    <td>
                      {record.spec.checkIn.location ? (
                        <div>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            {record.spec.checkIn.location.address || 'Location available'}
                          </div>
                          <div style={{ fontSize: '13px', color: '#999', marginTop: '2px' }}>
                            {record.spec.checkIn.location.latitude.toFixed(6)}, {record.spec.checkIn.location.longitude.toFixed(6)}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                      )}
                    </td>
                    <td>
                      {record.spec.checkOut?.time ? formatDateTime(record.spec.checkOut.time) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                      )}
                    </td>
                    <td>
                      {record.spec.checkOut?.image ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <img
                            src={record.spec.checkOut.image}
                            alt="Check-out"
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border: '1px solid #ddd',
                              cursor: 'pointer',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(record.spec.checkOut?.image, '_blank');
                            }}
                            title="Click to view full image"
                          />
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                      )}
                    </td>
                    <td>
                      {record.spec.checkOut?.location ? (
                        <div>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            {record.spec.checkOut.location.address || 'Location available'}
                          </div>
                          <div style={{ fontSize: '13px', color: '#999', marginTop: '2px' }}>
                            {record.spec.checkOut.location.latitude.toFixed(6)}, {record.spec.checkOut.location.longitude.toFixed(6)}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                      )}
                    </td>
                    <td>{getDuration(record.spec.checkIn.time, record.spec.checkOut?.time)}</td>
                    <td>
                      <span className={`status-badge status-${record.spec.status}`}>
                        {record.spec.status === 'checkedIn' ? 'Checked In' : 'Checked Out'}
                      </span>
                    </td>
                    <td>
                      {record.spec.status === 'checkedIn' ? (
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAdminCheckoutRecord(record);
                            setAdminCheckoutError('');
                            setAdminCheckoutTime(getNowDatetimeLocal());
                            const fallbackFromRecord =
                              record.spec.checkIn.location
                                ? {
                                    latitude: record.spec.checkIn.location.latitude,
                                    longitude: record.spec.checkIn.location.longitude,
                                    address: record.spec.checkIn.location.address,
                                  }
                                : { latitude: 20.5937, longitude: 78.9629, address: undefined as string | undefined };
                            fetchHospitalDefaultLocation().then((hospitalLoc) => {
                              if (hospitalLoc) {
                                setAdminCheckoutLocation(hospitalLoc);
                              } else {
                                setAdminCheckoutLocation(fallbackFromRecord);
                              }
                            });
                            if (!currentUser?.tenantId) {
                              setAdminCheckoutLocation(
                                record.spec.checkIn.location
                                  ? {
                                      latitude: record.spec.checkIn.location.latitude,
                                      longitude: record.spec.checkIn.location.longitude,
                                      address: record.spec.checkIn.location.address,
                                    }
                                  : { latitude: 20.5937, longitude: 78.9629, address: undefined }
                              );
                            }
                          }}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 4,
                            border: '1px solid #0ea5e9',
                            background: '#0ea5e9',
                            color: '#ffffff',
                            fontSize: 13,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Check-out
                        </button>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewRecord && (
        <div
          className="drawer-overlay"
          onClick={() => setViewRecord(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Escape' && setViewRecord(null)}
          aria-label="Close drawer"
        >
          <div
            className="drawer"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '50vw',
              minWidth: '480px',
              maxWidth: '100%',
              maxHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div className="drawer-header">
              <h3 className="drawer-title">{getUserName(viewRecord.spec.userId)}</h3>
              <button
                type="button"
                className="drawer-close"
                onClick={() => setViewRecord(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="drawer-body" style={{ padding: '16px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, minHeight: 0 }}>
                {/* Row 2: Status and Duration in one row */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', flex: 1, alignItems: 'center', gap: '8px' }}>
                    <span
                      style={{
                        fontSize: '12px',
                        opacity: 0.8,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Status:
                    </span>
                    <span
                      className={`status-badge status-${viewRecord.spec.status}`}
                      style={{
                        backgroundColor:
                          viewRecord.spec.status === 'checkedIn' ? '#22c55e' : '#ef4444',
                        color: '#ffffff',
                      }}
                    >
                      {viewRecord.spec.status === 'checkedIn' ? 'Checked In' : 'Checked Out'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flex: 1, alignItems: 'center', gap: '8px' }}>
                    <span
                      style={{
                        fontSize: '12px',
                        opacity: 0.8,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Duration:
                    </span>
                    <span style={{ fontSize: '14px' }}>
                      {getDuration(viewRecord.spec.checkIn.time, viewRecord.spec.checkOut?.time)}
                    </span>
                  </div>
                </div>

                {/* Row 3: Check-in and Check-out in 2 columns */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {/* Left column: Check-in */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', borderRadius: '8px', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Check-in</div>
                    {/* Time/Location column and Image column in one row */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {/* Left column: Time and Location stacked */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                          <label style={{ fontSize: '11px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Time</label>
                          <div style={{ marginTop: '1px' }}>{formatDateTime(viewRecord.spec.checkIn.time)}</div>
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Location</label>
                          <div style={{ marginTop: '1px', fontSize: '13px' }}>
                            {viewRecord.spec.checkIn.location ? (viewRecord.spec.checkIn.location.address || 'Location available') : '—'}
                          </div>
                        </div>
                      </div>
                      {/* Right column: Image */}
                      <div>
                        <label style={{ fontSize: '11px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Image</label>
                        <div style={{ marginTop: '3px' }}>
                          {viewRecord.spec.checkIn.image ? (
                            <img
                              src={viewRecord.spec.checkIn.image}
                              alt="Check-in"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '140px',
                                objectFit: 'contain',
                                borderRadius: '6px',
                                border: `1px solid ${isDark ? '#334155' : '#ddd'}`,
                                cursor: 'pointer',
                              }}
                              onClick={() => window.open(viewRecord.spec.checkIn.image, '_blank')}
                              role="presentation"
                            />
                          ) : (
                            <span style={{ color: 'inherit', opacity: 0.7 }}>—</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Right column: Check-out - always show Time, Image, Location; dash if not available */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', borderRadius: '8px', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Check-out</div>
                    {/* Time/Location column and Image column in one row */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {/* Left column: Time and Location stacked */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                          <label style={{ fontSize: '11px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Time</label>
                          <div style={{ marginTop: '1px' }}>
                            {viewRecord.spec.checkOut?.time ? formatDateTime(viewRecord.spec.checkOut.time) : '—'}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Location</label>
                          <div style={{ marginTop: '1px', fontSize: '13px' }}>
                            {viewRecord.spec.checkOut?.location ? (viewRecord.spec.checkOut.location.address || 'Location available') : '—'}
                          </div>
                        </div>
                      </div>
                      {/* Right column: Image */}
                      <div>
                        <label style={{ fontSize: '11px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Image</label>
                        <div style={{ marginTop: '3px' }}>
                          {viewRecord.spec.checkOut?.image ? (
                            <img
                              src={viewRecord.spec.checkOut.image}
                              alt="Check-out"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '140px',
                                objectFit: 'contain',
                                borderRadius: '6px',
                                border: `1px solid ${isDark ? '#334155' : '#ddd'}`,
                                cursor: 'pointer',
                              }}
                              onClick={() => window.open(viewRecord.spec.checkOut?.image, '_blank')}
                              role="presentation"
                            />
                          ) : (
                            <span style={{ color: 'inherit', opacity: 0.7 }}>—</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Map */}
                {(viewRecord.spec.checkIn.location || viewRecord.spec.checkOut?.location) && (
                  <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ marginTop: '4px', flex: 1, minHeight: 0 }}>
                      <AttendanceLocationsMap
                        isDark={isDark}
                        locations={[
                          ...(viewRecord.spec.checkIn.location
                            ? [{
                              lat: viewRecord.spec.checkIn.location.latitude,
                              lng: viewRecord.spec.checkIn.location.longitude,
                              label: 'Check-in',
                              color: 'green' as const,
                              time: formatDateTime(viewRecord.spec.checkIn.time),
                              address: viewRecord.spec.checkIn.location.address || 'Location available',
                              imageUrl: viewRecord.spec.checkIn.image || undefined,
                            }]
                            : []),
                          ...(viewRecord.spec.checkOut?.location
                            ? [{
                              lat: viewRecord.spec.checkOut.location.latitude,
                              lng: viewRecord.spec.checkOut.location.longitude,
                              label: 'Check-out',
                              color: 'red' as const,
                              time: viewRecord.spec.checkOut?.time ? formatDateTime(viewRecord.spec.checkOut.time) : '—',
                              address: viewRecord.spec.checkOut.location.address || 'Location available',
                              imageUrl: viewRecord.spec.checkOut.image || undefined,
                            }]
                            : []),
                        ]}
                        height="100%"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="drawer-footer">
              <button type="button" className="btn-cancel" onClick={() => setViewRecord(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {adminCheckoutRecord && (
        <div
          className="drawer-overlay"
          onClick={() => {
            if (!adminCheckoutLoading) {
              setAdminCheckoutRecord(null);
              setAdminCheckoutLocation(null);
              setAdminCheckoutError('');
            }
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !adminCheckoutLoading) {
              setAdminCheckoutRecord(null);
              setAdminCheckoutLocation(null);
              setAdminCheckoutError('');
            }
          }}
          aria-label="Close admin checkout drawer"
        >
          <div
            className="drawer"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '50vw',
              minWidth: '550px',
              maxWidth: '100%',
              maxHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div className="drawer-header">
              <h3 className="drawer-title">
                Check-out – {getUserName(adminCheckoutRecord.spec.userId)} (By Admin)
              </h3>
              <button
                type="button"
                className="drawer-close"
                onClick={() => {
                  if (!adminCheckoutLoading) {
                    setAdminCheckoutRecord(null);
                    setAdminCheckoutLocation(null);
                    setAdminCheckoutError('');
                  }
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div
              className="drawer-body"
              style={{
                padding: 16,
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                color: isDark ? '#e2e8f0' : '#1e293b',
              }}
            >
              {adminCheckoutError && (
                <div className="error-message" style={{ marginBottom: 12 }}>
                  {adminCheckoutError}
                </div>
              )}

              <div style={{ fontSize: 14, color: isDark ? '#94a3b8' : '#64748b' }}>
                <div>
                  <strong style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>Current status:</strong>{' '}
                  <span className={`status-badge status-${adminCheckoutRecord.spec.status}`}>
                    {adminCheckoutRecord.spec.status === 'checkedIn' ? 'Checked In' : 'Checked Out'}
                  </span>
                </div>
                <div style={{ marginTop: 4 }}>
                  <strong style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>Check-in time:</strong>{' '}
                  {formatDateTime(adminCheckoutRecord.spec.checkIn.time)}
                </div>
              </div>

              <div style={{ marginTop: 8 }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 500,
                    marginBottom: 4,
                    color: isDark ? '#e2e8f0' : '#1e293b',
                  }}
                >
                  Check-out time
                </label>
                <input
                  type="datetime-local"
                  max={getMaxDatetimeLocal()}
                  value={adminCheckoutTime}
                  onChange={(e) => setAdminCheckoutTime(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: isDark ? '1px solid #475569' : '1px solid #d1d5db',
                    background: isDark ? '#1e293b' : '#fff',
                    color: isDark ? '#f1f5f9' : '#1e293b',
                    fontSize: 14,
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 500,
                    marginBottom: 4,
                    color: isDark ? '#e2e8f0' : '#1e293b',
                  }}
                >
                  Check-out location
                </label>
                <p style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#64748b', marginBottom: 8 }}>
                  Use the place search or click on the map to set the check-out location.
                </p>
              </div>

              <div style={{ marginBottom: 4 }}>
                {/* Simple place search using Google Places */}
                {/* Re-use global PlaceSearch component via dynamic import to avoid extra wiring here */}
                {/* eslint-disable-next-line @typescript-eslint/no-var-requires */}
                {(() => {
                  // Lazy require to avoid circular imports at module load
                  const PlaceSearch =
                    // eslint-disable-next-line @typescript-eslint/no-require-imports
                    require('../components/PlaceSearch').default as React.ComponentType<{
                      onPlaceSelect: (lat: number, lng: number, address: string) => void;
                      placeholder?: string;
                    }>;
                  return (
                    <PlaceSearch
                      onPlaceSelect={(lat, lng, address) => {
                        setAdminCheckoutLocation({
                          latitude: lat,
                          longitude: lng,
                          address,
                        });
                        setAdminCheckoutError('');
                      }}
                      placeholder="Search check-out location…"
                    />
                  );
                })()}
              </div>

              <div style={{ flex: 1, minHeight: 0, marginTop: 8 }}>
                {(() => {
                  // Lazy require GoogleMap to avoid circular imports
                  // eslint-disable-next-line @typescript-eslint/no-var-requires
                  const GoogleMap =
                    // eslint-disable-next-line @typescript-eslint/no-require-imports
                    require('../components/GoogleMap').default as React.ComponentType<{
                      center: { lat: number; lng: number };
                      location?: { latitude: number; longitude: number; address?: string } | null;
                      onLocationChange?: (lat: number, lng: number) => void;
                      height?: string;
                      markerIconUrl?: string;
                    }>;

                  const fallbackCenter = { lat: 20.5937, lng: 78.9629 }; // India center
                  const mapCenter = adminCheckoutLocation
                    ? { lat: adminCheckoutLocation.latitude, lng: adminCheckoutLocation.longitude }
                    : adminCheckoutRecord.spec.checkOut?.location
                      ? {
                        lat: adminCheckoutRecord.spec.checkOut.location.latitude,
                        lng: adminCheckoutRecord.spec.checkOut.location.longitude,
                      }
                      : adminCheckoutRecord.spec.checkIn.location
                        ? {
                          lat: adminCheckoutRecord.spec.checkIn.location.latitude,
                          lng: adminCheckoutRecord.spec.checkIn.location.longitude,
                        }
                        : fallbackCenter;

                  return (
                    <GoogleMap
                      center={mapCenter}
                      location={
                        adminCheckoutLocation ||
                        (adminCheckoutRecord.spec.checkOut?.location as
                          | { latitude: number; longitude: number; address?: string }
                          | undefined) ||
                        (adminCheckoutRecord.spec.checkIn.location as
                          | { latitude: number; longitude: number; address?: string }
                          | undefined) ||
                        null
                      }
                      onLocationChange={(lat, lng) => {
                        setAdminCheckoutLocation((prev) => ({
                          latitude: lat,
                          longitude: lng,
                          address: prev?.address,
                        }));
                        setAdminCheckoutError('');
                      }}
                      height="100%"
                      markerIconUrl="/images/end_marker.png"
                    />
                  );
                })()}
              </div>
            </div>
            <div className="drawer-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  if (!adminCheckoutLoading) {
                    setAdminCheckoutRecord(null);
                    setAdminCheckoutLocation(null);
                    setAdminCheckoutTime('');
                    setAdminCheckoutError('');
                  }
                }}
                disabled={adminCheckoutLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                disabled={!adminCheckoutLocation || !adminCheckoutTime || adminCheckoutLoading}
                onClick={async () => {
                  if (!adminCheckoutRecord || !adminCheckoutLocation || !adminCheckoutTime) return;
                  try {
                    setAdminCheckoutLoading(true);
                    setAdminCheckoutError('');
                    const outTimeMs = new Date(adminCheckoutTime).getTime();
                    const lastCheckInTime = adminCheckoutRecord.spec.checkIn?.time;

                    if (!outTimeMs || Number.isNaN(outTimeMs)) {
                      setAdminCheckoutError('Please select a valid check-out date & time.');
                      setAdminCheckoutLoading(false);
                      return;
                    }

                    if (lastCheckInTime && outTimeMs < lastCheckInTime) {
                      setAdminCheckoutError('Check-out date & time cannot be earlier than the last check-in time.');
                      setAdminCheckoutLoading(false);
                      return;
                    }

                    await checkOut(
                      adminCheckoutRecord.spec.userId,
                      adminCheckoutLocation.latitude,
                      adminCheckoutLocation.longitude,
                      undefined,
                      outTimeMs
                    );
                    await fetchAttendance();
                    setAdminCheckoutRecord(null);
                    setAdminCheckoutLocation(null);
                    setAdminCheckoutTime('');
                  } catch (err: any) {
                    // eslint-disable-next-line no-console
                    console.error('Admin check-out failed', err);
                    setAdminCheckoutError(
                      err?.response?.data?.message || 'Failed to perform admin check-out'
                    );
                  } finally {
                    setAdminCheckoutLoading(false);
                  }
                }}
              >
                {adminCheckoutLoading ? 'Checking out…' : 'Confirm Check-out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AttendanceManagement;

